// directives/intersect.js
const elementConfigMap = new WeakMap()
const observerCache = new Map()
const rootCache = new Map()
const rootIdMap = new WeakMap()
const pendingUpdates = new Map()

let rootUid = 0
let rafId = 0

const EMPTY_OBJ = Object.freeze(Object.create(null))

const HIDDEN_STYLE = {
  opacity: '0',
  visibility: 'hidden',
  willChange: 'opacity, visibility'
}

const DEFAULT_CONFIG = Object.freeze({
  class: '',
  initialClass: 'Heziyun-hidden',
  delay: 0,
  once: true,
  threshold: 0.1,
  rootMargin: '0px',
  root: null,
  handler: null,
  useStyleHidden: true,
  transitionDuration: '',
  transitionProperty: ''
})

const supportsIO = typeof window !== 'undefined' && 'IntersectionObserver' in window
const supportsRIC = typeof window !== 'undefined' && 'requestIdleCallback' in window

export const intersectDirective = {
  mounted(el, binding) {
    mountElement(el, binding)
  },

  updated(el, binding) {
    updateElement(el, binding)
  },

  unmounted(el) {
    cleanupElement(el)
  },

  // 兼容 Vue2
  inserted(el, binding) {
    mountElement(el, binding)
  },

  componentUpdated(el, binding) {
    updateElement(el, binding)
  },

  unbind(el) {
    cleanupElement(el)
  }
}

function mountElement(el, binding) {
  const config = normalizeConfig(parseBinding(binding && binding.value))

  hideElement(el, config)

  if (!supportsIO) {
    handleUnsupportedBrowser(el, config)
    return
  }

  nextMicroTask(() => {
    if (el.isConnected) {
      setupIntersectionObserver(el, config)
    }
  })
}

function updateElement(el, binding) {
  const oldData = elementConfigMap.get(el)
  if (!oldData) return

  const newConfig = normalizeConfig(parseBinding(binding && binding.value))

  if (!isSameConfig(oldData.config, newConfig)) {
    cleanupElement(el)
    hideElement(el, newConfig)

    if (supportsIO) {
      setupIntersectionObserver(el, newConfig)
    } else {
      handleUnsupportedBrowser(el, newConfig)
    }
  }
}

function hideElement(el, config) {
  if (!el._intersectOriginalStyle) {
    el._intersectOriginalStyle = {
      opacity: el.style.opacity,
      visibility: el.style.visibility,
      transition: el.style.transition,
      transitionDuration: el.style.transitionDuration,
      transitionProperty: el.style.transitionProperty,
      willChange: el.style.willChange
    }
  }

  if (config.useStyleHidden) {
    Object.assign(el.style, HIDDEN_STYLE)
  } else if (config.initialClass) {
    el.classList.add(config.initialClass)
  }
}

function showElement(el, config) {
  const original = el._intersectOriginalStyle

  if (config.useStyleHidden) {
    el.style.opacity = original ? original.opacity : ''
    el.style.visibility = original ? original.visibility : ''
    el.style.willChange = original ? original.willChange : ''

    if (config.transitionDuration || config.transitionProperty) {
      el.style.transitionDuration = config.transitionDuration || '0.3s'
      el.style.transitionProperty = config.transitionProperty || 'opacity, visibility'
    } else if (original) {
      el.style.transition = original.transition
      el.style.transitionDuration = original.transitionDuration
      el.style.transitionProperty = original.transitionProperty
    }
  } else if (config.initialClass) {
    el.classList.remove(config.initialClass)
  }

  if (config.class) {
    el.classList.add(config.class)
  }
}

function setupIntersectionObserver(el, config) {
  const finalConfig = resolveRootConfig(config)
  const observer = getOrCreateObserver(finalConfig)

  const data = {
    config: finalConfig,
    observer,
    timer: null,
    triggered: false,
    cleanup() {
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }

      try {
        observer.unobserve(el)
      } catch (e) {}

      elementConfigMap.delete(el)

      observer._count = Math.max((observer._count || 1) - 1, 0)

      if (observer._count === 0) {
        observer.disconnect()
        observerCache.delete(getObserverKey(finalConfig))
      }
    }
  }

  elementConfigMap.set(el, data)
  observer._count = (observer._count || 0) + 1

  requestAnimationFrame(() => {
    if (!el.isConnected) return

    try {
      observer.observe(el)
    } catch (error) {
      console.error('IntersectionObserver observe error:', error)
      fallbackToVisible(el, finalConfig)
    }
  })
}

function normalizeConfig(config) {
  const merged = Object.assign({}, DEFAULT_CONFIG, config || EMPTY_OBJ)

  if (Array.isArray(merged.threshold)) {
    merged.threshold = merged.threshold
      .map(toNumber)
      .map(clamp01)
      .sort((a, b) => a - b)
  } else {
    merged.threshold = clamp01(toNumber(merged.threshold))
  }

  merged.delay = Math.max(0, parseInt(merged.delay, 10) || 0)
  merged.rootMargin = String(merged.rootMargin || '0px')
  merged.once = merged.once !== false
  merged.useStyleHidden = merged.useStyleHidden !== false

  return Object.freeze(merged)
}

function parseBinding(value) {
  if (!value) return EMPTY_OBJ

  if (typeof value === 'string') {
    return { class: value }
  }

  if (typeof value === 'function') {
    return { handler: value }
  }

  if (typeof value === 'object') {
    return value
  }

  return EMPTY_OBJ
}

function resolveRootConfig(config) {
  if (typeof config.root !== 'string') {
    return config
  }

  const root = getRootElement(config.root)

  return Object.freeze(
    Object.assign({}, config, {
      root
    })
  )
}

function getRootElement(selector) {
  if (!selector) return null

  if (rootCache.has(selector)) {
    const cached = rootCache.get(selector)
    if (cached && cached.isConnected) return cached
    rootCache.delete(selector)
  }

  const root = document.querySelector(selector)

  if (root) {
    rootCache.set(selector, root)
  }

  return root || null
}

function getObserverKey(config) {
  const thresholdKey = Array.isArray(config.threshold)
    ? config.threshold.join(',')
    : String(config.threshold)

  return [
    getRootId(config.root),
    config.rootMargin,
    thresholdKey
  ].join('|')
}

function getRootId(root) {
  if (!root) return 'null'

  if (!rootIdMap.has(root)) {
    rootIdMap.set(root, `root_${++rootUid}`)
  }

  return rootIdMap.get(root)
}

function getOrCreateObserver(config) {
  const key = getObserverKey(config)

  if (observerCache.has(key)) {
    return observerCache.get(key)
  }

  const observer = new IntersectionObserver(handleObserverCallback, {
    root: config.root || null,
    rootMargin: config.rootMargin,
    threshold: Array.isArray(config.threshold) ? config.threshold : [config.threshold]
  })

  observer._count = 0
  observerCache.set(key, observer)

  return observer
}

function handleObserverCallback(entries) {
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]

    if (entry.isIntersecting) {
      processIntersection(entry)
    }
  }
}

function processIntersection(entry) {
  const el = entry.target
  const data = elementConfigMap.get(el)

  if (!data || data.triggered || !el.isConnected) return

  const config = data.config

  const trigger = () => {
    if (!el.isConnected) return

    data.triggered = true
    scheduleVisualUpdate(el, config)

    if (typeof config.handler === 'function') {
      try {
        config.handler(el, entry)
      } catch (error) {
        console.error('Intersection handler error:', error)
      }
    }

    if (config.once) {
      data.cleanup()
    }
  }

  if (config.delay > 0) {
    if (data.timer) clearTimeout(data.timer)
    data.timer = setTimeout(trigger, config.delay)
  } else {
    trigger()
  }
}

function scheduleVisualUpdate(el, config) {
  pendingUpdates.set(el, config)

  if (!rafId) {
    rafId = requestAnimationFrame(processPendingUpdates)
  }
}

function processPendingUpdates() {
  rafId = 0

  pendingUpdates.forEach((config, el) => {
    if (el.isConnected) {
      showElement(el, config)
    }
  })

  pendingUpdates.clear()
}

function cleanupElement(el) {
  const data = elementConfigMap.get(el)

  if (data) {
    data.cleanup()
  }

  pendingUpdates.delete(el)
}

function handleUnsupportedBrowser(el, config) {
  console.warn('当前浏览器不支持 IntersectionObserver，已使用降级显示方案')
  scheduleVisualUpdate(el, config)

  if (typeof config.handler === 'function') {
    safeCallHandler(config.handler, el, { isIntersecting: true, target: el })
  }
}

function fallbackToVisible(el, config) {
  scheduleTask(() => {
    if (!el.isConnected) return

    scheduleVisualUpdate(el, config)

    if (typeof config.handler === 'function') {
      safeCallHandler(config.handler, el, { isIntersecting: true, target: el })
    }
  })
}

function safeCallHandler(handler, el, entry) {
  try {
    handler(el, entry)
  } catch (error) {
    console.error('Intersection handler error:', error)
  }
}

function scheduleTask(callback) {
  if (supportsRIC) {
    requestIdleCallback(callback, { timeout: 50 })
  } else {
    nextMicroTask(callback)
  }
}

function nextMicroTask(callback) {
  if (typeof Promise !== 'undefined') {
    Promise.resolve().then(callback)
  } else {
    setTimeout(callback, 0)
  }
}

function isSameConfig(a, b) {
  if (a === b) return true

  return (
    a.class === b.class &&
    a.initialClass === b.initialClass &&
    a.delay === b.delay &&
    a.once === b.once &&
    a.rootMargin === b.rootMargin &&
    a.root === b.root &&
    a.handler === b.handler &&
    a.useStyleHidden === b.useStyleHidden &&
    a.transitionDuration === b.transitionDuration &&
    a.transitionProperty === b.transitionProperty &&
    thresholdEqual(a.threshold, b.threshold)
  )
}

function thresholdEqual(a, b) {
  const aIsArray = Array.isArray(a)
  const bIsArray = Array.isArray(b)

  if (aIsArray !== bIsArray) return false

  if (!aIsArray) return a === b

  if (a.length !== b.length) return false

  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }

  return true
}

function toNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

function clamp01(value) {
  return Math.min(1, Math.max(0, value))
}